/**
 * useSocialProof — shared hook that batch-fetches social-proof signals
 * for a list of car ids and exposes a `getProofFor(carId)` selector.
 *
 * Iter 69 — drives the "Booked Nm ago" and "X watching now" badges on
 * Browse cards and CarDetail. One network round-trip per page, keyed by
 * the joined+sorted car id list so React Strict-Mode double-renders or
 * unrelated state updates don't refetch.
 *
 * Returns a stable selector function so consumers can call
 *   const { last_booked_minutes_ago, viewers_now } = getProofFor(car.id);
 * without forcing parent re-renders when an unrelated car changes.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

export default function useSocialProof(carIds, { refreshMs = 30000 } = {}) {
  const [byId, setById] = useState({});

  // Stable cache key — sort to avoid order-induced refetches.
  const cacheKey = useMemo(
    () => (carIds || []).slice().sort().join(","),
    [carIds]
  );

  useEffect(() => {
    if (!cacheKey) {
      setById({});
      return undefined;
    }
    let cancelled = false;
    let timerId = null;

    const fetchOnce = async () => {
      if (cancelled) return;
      const ids = cacheKey.split(",");
      // Backend caps at 100 ids per request — chunk if necessary.
      const chunks = [];
      for (let i = 0; i < ids.length; i += 100) {
        chunks.push(ids.slice(i, i + 100));
      }
      try {
        const results = await Promise.all(
          chunks.map((chunk) =>
            api
              .post("/cars/social-proof", { car_ids: chunk })
              .then((r) => r.data || {})
              .catch(() => ({}))
          )
        );
        if (cancelled) return;
        setById(Object.assign({}, ...results));
      } catch {
        if (!cancelled) setById({});
      }
    };

    fetchOnce();
    // Iter 69 — repoll every `refreshMs` (default 30s) so the badge
    // reflects renters joining/leaving the car as heartbeats arrive.
    // Skip the interval when refreshMs <= 0 (consumer opted out).
    if (refreshMs > 0) {
      timerId = setInterval(fetchOnce, refreshMs);
    }

    return () => {
      cancelled = true;
      if (timerId) clearInterval(timerId);
    };
    // We only depend on cacheKey + refreshMs — the actual carIds reference
    // may change every render in the parent without affecting the data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, refreshMs]);

  return useMemo(
    () => ({
      getProofFor: (carId) => byId[carId] || null,
    }),
    [byId]
  );
}
